import { isNilOrEmpty } from 'ramda-extension';
import { createAction } from 'redux-actions';

import { replaceData, resetForm, setValue } from '@gef-ui/form-core/stream/transfer/control';

import { appendMode, USER_ROLES } from 'constants/general';
import dialog from 'features/common/dialog';
import fetch from 'features/common/fetch';
import { pushToFilterDataLayer } from 'features/common/gtm/utils';
import { subheaders } from 'features/common/Subheader/constants';
import { setVisibleState } from 'features/common/Subheader/slice';
import campaignDetail from 'features/views/campaignManagement/campaignDetail';
// eslint-disable-next-line import/no-cycle
import contactReleasing from 'features/views/campaignManagement/contactReleasing';
import contactStates from 'features/views/campaignManagement/contactStates';
import user from 'features/views/user';
import { cleanse, generateCommonFetchActions, getRoutePath, history, trimFormData } from 'utils';

import { reset as resetCampaignDetail } from '../campaignDetail/actions';
import { CAMPAIGN_MANAGEMENT_FORM_NAME, FIELDS } from '../campaignDetail/form';
import { reset as resetNominatedSellers } from '../nominatedSellers/actions';
import { CAMPAIGN_MANAGEMENT_SIDEBAR_FORM_NAME } from '../sidebar/form';
import * as types from './actionTypes';
import { formDataFieldsMap, quickFilterOptions, rolesToSubmit } from './constants';
import { initialState } from './reducer';
import { getQuickFilterState, getResetRelatedSections, getSort } from './selectors';
import { paramsFactory } from './utils';

const appendContacts = createAction(types.APPEND_CONTACTS, (contacts) => ({ contacts }));
const reset = createAction(types.RESET);
const resetSelectedContacts = createAction(types.RESET_SELECTED_CONTACTS);
const setContacts = createAction(types.SET_CONTACTS, (contacts) => ({ contacts }));
const setQuickFilter = createAction(types.SET_QUICK_FILTER, (quickFilter) => ({ quickFilter }));
const toggleContact = createAction(types.TOGGLE_CONTACT, (id) => ({ id }));
const setSort = createAction(types.SET_SORT, (sort) => ({ sort }));
export const setResetRelatedSections = createAction(types.SET_RESET_RELATED_SECTIONS, (resetRelatedSections) => ({
	resetRelatedSections,
}));

const url = fetch.constants.urls.contactList;

const fetchContacts =
	({ queryParams, body, callGtmFilter }, append) =>
	async (dispatch, getState, { getFormData }) => {
		const formData = await getFormData(CAMPAIGN_MANAGEMENT_FORM_NAME);
		const finalFormData = { ...formData };

		dispatch(
			fetch.actions.post(url, {
				takeLatest: true,
				body,
				queryParams,
				onSuccess: [
					(response) => () => {
						const data = response.body.data;
						if (data && Array.isArray(data)) {
							dispatch(append === appendMode.APPEND ? appendContacts(data) : setContacts(data));

							if (callGtmFilter) {
								const quickFilterState = getQuickFilterState(getState());
								if (quickFilterState === quickFilterOptions.TO_BE_RELEASED) {
									finalFormData.quickFilterToRelease = true;
								}

								if (quickFilterState === quickFilterOptions.TO_BE_ASSIGNED) {
									finalFormData.quickFilterToAssign = true;
								}

								pushToFilterDataLayer({
									filterSection: 'Řízení kampaní - kontakty',
									formData: cleanse(finalFormData),
									formDataFieldsMap,
								});
							}
						}
					},
				],
			}),
		);
	};

const { loadMore, loadPage, reload, loadSorted } = generateCommonFetchActions(fetchContacts, url);

export const sortRecords = (sort) => (dispatch) => {
	dispatch(setSort(sort));
	dispatch(loadSorted(sort));
};

const changeQuickFilter = (quickFilter) => (dispatch, getState) => {
	const { queryParams, body } = fetch.selectors.getLastRequest(fetch.constants.urls.contactList)(getState());

	const params = {
		queryParams: {
			...queryParams,
			page: 0,
		},
		body: {
			...body,
			onlyToAssign: quickFilter === quickFilterOptions.TO_BE_ASSIGNED || undefined,
			onlyToRelease: quickFilter === quickFilterOptions.TO_BE_RELEASED || undefined,
		},
		callGtmFilter: true,
	};
	dispatch(setQuickFilter(quickFilter));
	dispatch(fetchContacts(params, appendMode.SET));
};

const submitFilter =
	() =>
	async (dispatch, getState, { getFormData }) => {
		const state = getState();
		const campaignId = campaignDetail.selectors.getCampaignId(state);
		const userRole = user.selectors.getCurrentRole(state);
		const data = await getFormData(CAMPAIGN_MANAGEMENT_FORM_NAME);
		const formData = trimFormData(data);
		const sort = getSort(state);
		const params = paramsFactory(campaignId, userRole, formData, sort);
		const fetchContactsParams = {
			...params,
			callGtmFilter: true,
		};

		dispatch(setQuickFilter(initialState.quickFilter));
		dispatch(fetchContacts(fetchContactsParams, appendMode.SET));
		dispatch(contactStates.actions.fetchContactStateCount(params));
	};

const resetCriteria = () => (dispatch, getState) => {
	const userRole = user.selectors.getCurrentRole(getState());
	dispatch(resetState(userRole));
	dispatch(startupContacts());
};

const resetState =
	(userRole) =>
	(dispatch, getState, { sendControlNext }) => {
		dispatch(reset());
		// temp fix - reset the whole form for guarantor
		userRole === USER_ROLES.GUARANTOR && sendControlNext(CAMPAIGN_MANAGEMENT_FORM_NAME, resetForm());
		dispatch(contactReleasing.actions.reset());
		dispatch(contactStates.actions.reset());
		dispatch(fetch.actions.clearFetchStore(url));
		sendControlNext(CAMPAIGN_MANAGEMENT_SIDEBAR_FORM_NAME, resetForm());
		dispatch(setVisibleState({ id: subheaders.campaignManagementSidebarManualAssign, visible: false }));
		dispatch(dialog.actions.clear());
	};

const startupContacts =
	(injectedCampaignId) =>
	async (dispatch, getState, { getFormData, sendControlNext }) => {
		const state = getState();
		const campaignId = Number(injectedCampaignId) || campaignDetail.selectors.getCampaignId(state);
		const userRole = user.selectors.getCurrentRole(state);
		const sort = getSort(state);
		const userStructure = user.selectors.getRoleStructure(state);
		const isGuarantor = userRole === USER_ROLES.GUARANTOR;
		if (!userStructure) return;

		const { role, channelId, regionId, agencyId, managerLogin, sellerLogin, ppzLogin } = userStructure;

		// temp fix - if guarantor, dont create formData from userStructure, but use the real "form" data
		const formData = isGuarantor
			? trimFormData(await getFormData(CAMPAIGN_MANAGEMENT_FORM_NAME))
			: {
					channel: channelId,
					region: regionId,
					agency: agencyId,
					manager: managerLogin,
					seller: sellerLogin,
					ppz: ppzLogin,
			  };

		// temp fix - resetAndReplace formData only to non guarantor roles
		if (!isGuarantor) {
			sendControlNext(CAMPAIGN_MANAGEMENT_FORM_NAME, resetForm());
			sendControlNext(CAMPAIGN_MANAGEMENT_FORM_NAME, replaceData({ data: formData }));

			const searchParams = new URLSearchParams(window.location.search);
			const contactStateQuery = searchParams.get('contactState');

			if (!isNilOrEmpty(contactStateQuery)) {
				sendControlNext(
					CAMPAIGN_MANAGEMENT_FORM_NAME,
					setValue({ fieldName: FIELDS.contactState, value: contactStateQuery }),
				);
				formData.contactState = contactStateQuery;
				history.replace(getRoutePath('campaignManagement', { campaignId }));
			}
		}

		const params = paramsFactory(campaignId, userRole, formData, sort);
		dispatch(contactStates.actions.fetchContactStateCount(params));

		if (rolesToSubmit.includes(role)) {
			dispatch(setVisibleState({ id: subheaders.campaignManagement, visible: false }));
			dispatch(fetchContacts(params, appendMode.SET));
		} else {
			dispatch(setVisibleState({ id: subheaders.campaignManagement, visible: true }));
		}
	};

export const possiblyResetRelatedSections = () => (dispatch, getState) => {
	const resetRelatedSections = getResetRelatedSections(getState());

	if (resetRelatedSections) {
		dispatch(resetNominatedSellers());
		dispatch(resetCampaignDetail());
		dispatch(resetState());
		dispatch(setResetRelatedSections(true));
	}
};

export const prefillFormWithPreviousData =
	() =>
	(dispatch, getState, { sendControlNext }) => {
		const fetchStorePath = fetch.constants.urls.contactList;
		const lastRequest = fetch.selectors.getLastRequest(fetchStorePath)(getState());
		if (!isNilOrEmpty(lastRequest)) {
			const { body } = lastRequest;

			const { orgStruct, solutionStateIds = [] } = body;

			const data = {
				channel: orgStruct?.channelId,
				region: orgStruct?.regionId,
				...(!isNilOrEmpty(orgStruct?.agencyIds) && { agency: orgStruct?.agencyIds[0] }),
				manager: orgStruct?.manager,
				seller: orgStruct?.seller,
				ppz: orgStruct?.ppz,
				policyNumber: body?.policyNo,
				policyEndDate: body?.policyEndMonth,
				product: body?.productId,
				...(!isNilOrEmpty(solutionStateIds) && { contactState: String(solutionStateIds[0]) }),
				assignLevel: body?.assignLevel,
				firstName: body?.clientName,
				surname: body?.clientSurname,
				businessName: body?.businessName,
				personIn: body?.personIn,
				businessIn: body?.businessIn,
				municipality: body?.municipality,
				zipCode: body?.zipCode,
				policyAgent: body?.policyAgentSellerName,
				printType: body?.printTypeId,
			};

			sendControlNext(CAMPAIGN_MANAGEMENT_FORM_NAME, replaceData({ data }));
		}
	};

export const actions = {
	appendContacts,
	changeQuickFilter,
	loadMore,
	loadPage,
	loadSorted,
	sortRecords,
	reload,
	reset,
	resetCriteria,
	resetSelectedContacts,
	resetState,
	setContacts,
	setQuickFilter,
	startupContacts,
	submitFilter,
	toggleContact,
};
